import type { Question, ResponsePayload, ThemeAnswer } from 'types';
import { normalizeQuestionType } from 'utils/questionTypeUtils';

/**
 * The learner's own answers in the shape the Life Plan book writes onto its pages: plain text, list items,
 * a rating or a true/false flag. Only the Life Plan theme receives these (see `ThemeContextValue.lifePlan`).
 */
const clean = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const optionLabels = (question: Question, ids: readonly number[] | undefined, other: string) => {
  const options = question.options ?? [];
  return (ids ?? []).map((id) => {
    const option = options.find((candidate) => candidate.id === id);
    if (!option) return '';
    return option.is_other && other ? other : clean(option.front_text);
  }).filter(Boolean);
};

/** A saved response or a draft on the open card, as display values; null when there is nothing to show yet. */
export function themeAnswerFromResponse(question: Question | null | undefined, response: ResponsePayload | null | undefined): ThemeAnswer | null {
  if (!question || !response) return null;
  const type = normalizeQuestionType(question.question_type_display ?? question.question_type ?? '');
  const other = clean(response.custom_other_value);
  switch (type) {
    case 'text_or_audio':
    case 'callback_summary': {
      const text = clean(response.text_value);
      return text ? { text, items: [], rating: null, flag: null } : null;
    }
    case 'text_list': {
      const items = (response.text_list ?? []).map(clean);
      return items.some(Boolean) ? { text: items.filter(Boolean).join(', '), items, rating: null, flag: null } : null;
    }
    case 'rating':
      return typeof response.rating_value === 'number' && Number.isFinite(response.rating_value)
        ? { text: String(response.rating_value), items: [], rating: response.rating_value, flag: null } : null;
    case 'boolean':
    case 'styled_boolean':
      return typeof response.boolean_value === 'boolean'
        ? { text: response.boolean_value ? 'True' : 'False', items: [], rating: null, flag: response.boolean_value } : null;
    case 'multiple_choice':
    case 'multiple_select':
    case 'horizontal_drag_drop': {
      const items = optionLabels(question, response.selected_options, other);
      return items.length ? { text: items.join(', '), items, rating: null, flag: null } : null;
    }
    case 'multiple_reveal': {
      const items = optionLabels(question, response.revealed_options, '');
      return items.length ? { text: items.join(', '), items, rating: null, flag: null } : null;
    }
    default:
      return null;
  }
}

/** The module's saved answers by question key (the current run; earlier modules come from the server). */
export function themeAnswersFromResponses(questions: readonly Question[] | undefined, responses: Record<number, ResponsePayload | undefined>): Record<string, ThemeAnswer> {
  const answers: Record<string, ThemeAnswer> = {};
  (questions ?? []).forEach((question) => {
    if (!question.key || typeof question.id !== 'number') return;
    const response = responses[question.id];
    if (!response || response.is_completed === false) return;
    const answer = themeAnswerFromResponse(question, response);
    if (answer) answers[question.key] = answer;
  });
  return answers;
}

/**
 * What the learner is writing on the open card right now. Drafts change on every keystroke, so they bypass the
 * React theme context: the player writes here (Life Plan theme only) and the book reads it each frame.
 */
export interface LifePlanLiveAnswer { key: string; answer: ThemeAnswer | null; version: number }
let live: LifePlanLiveAnswer | null = null;
let version = 0;

export const setLifePlanLiveAnswer = (key: string, answer: ThemeAnswer | null) => {
  version += 1;
  live = { key, answer, version };
};
export const clearLifePlanLiveAnswer = () => {
  version += 1;
  live = null;
};
export const lifePlanLiveAnswer = (): LifePlanLiveAnswer | null => live;
